// Einstellungen → Postfächer & Signaturen (D-060): Liste mit Status, „Postfach hinzufügen“ als
// Selbstbedienung (Passwort verschlüsselt auf dem Server), Anzeigename/Farbe ändern, Passwort erneuern,
// Postfach entfernen. Signaturen bearbeitet weiterhin die SettingsPage.
import { useEffect, useState, type FormEvent } from 'react';
import {
  MAILBOX_COLORS,
  MailboxCreateSchema,
  type Mailbox,
  type MailboxCreate,
} from '@mail/shared';
import { api } from '../lib/api.ts';
import { loadCounts, reloadMailboxes, useStore } from '../lib/store.ts';
import { toast } from './Toast.tsx';

const FARBEN: Record<(typeof MAILBOX_COLORS)[number], string> = {
  green: 'grün',
  magenta: 'magenta',
  sky: 'hellblau',
  blue: 'blau',
  red: 'rot',
  yellow: 'gelb',
};

// „verbindet …“: angelegt, aber der Worker hat sich noch nicht gemeldet (weder Erfolg noch Fehler)
export const verbindetGerade = (mb: Mailbox) =>
  mb.syncEnabled && !mb.lastSyncAt && !mb.syncError && mb.credentials === 'db';

export function postfachStatus(mb: Mailbox): { text: string; ton: 'ok' | 'warn' | 'wait' | 'off' } {
  if (!mb.syncEnabled) return { text: 'noch nicht angebunden', ton: 'off' };
  if (mb.syncError) return { text: `⚠ ${mb.syncError}`, ton: 'warn' };
  if (!mb.lastSyncAt) return { text: 'verbindet …', ton: 'wait' };
  const t = new Date(mb.lastSyncAt);
  return {
    text: `läuft · zuletzt ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
    ton: 'ok',
  };
}

export function PostfachStatus({ mb }: { mb: Mailbox }) {
  const s = postfachStatus(mb);
  return (
    <span className={`pf-status pf-${s.ton}`} data-status={s.ton}>
      {s.ton === 'wait' && <span className="pf-spin" aria-hidden />}
      {s.text}
    </span>
  );
}

// ---------- Ein vorhandenes Postfach: Name, Farbe, Passwort, Entfernen ----------
export function PostfachBearbeiten({ mb }: { mb: Mailbox }) {
  const [name, setName] = useState(mb.displayName);
  const [color, setColor] = useState(mb.color);
  const [pwOffen, setPwOffen] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(mb.displayName);
    setColor(mb.color);
  }, [mb.displayName, mb.color]);
  if (!mb.editable) return null;
  const geaendert = name.trim() !== mb.displayName || color !== mb.color;
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast(ok);
      await reloadMailboxes();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pf-edit">
      <div className="konto-row">
        <label>
          Anzeigename
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </label>
        <label>
          Farbe
          <select value={color} onChange={(e) => setColor(e.target.value)}>
            {MAILBOX_COLORS.map((c) => (
              <option key={c} value={c}>
                {FARBEN[c]}
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn sm"
          disabled={busy || !geaendert || !name.trim()}
          onClick={() =>
            void run(
              () =>
                api.mailboxPatch(mb.id, {
                  displayName: name.trim(),
                  color: color as MailboxCreate['color'],
                }),
              'Gespeichert',
            )
          }
        >
          Speichern
        </button>
        {mb.credentials === 'db' && (
          <>
            <button className="btn sm" disabled={busy} onClick={() => setPwOffen((v) => !v)}>
              Passwort erneuern
            </button>
            <button
              className="btn sm danger"
              disabled={busy}
              onClick={() => {
                if (
                  !confirm(
                    `${mb.address} entfernen?\n\nDer Sync stoppt und der Spiegel in der Mail-App (Threads, Nachrichten, Signaturen, Zuweisungen) wird gelöscht. Die Mails bei Goneo bleiben unberührt.`,
                  )
                )
                  return;
                void run(() => api.mailboxDelete(mb.id), `${mb.address} entfernt`);
              }}
            >
              Entfernen
            </button>
          </>
        )}
      </div>
      {pwOffen && (
        <form
          className="konto-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (!pw) return;
            void run(() => api.mailboxPatch(mb.id, { password: pw }), 'Passwort gespeichert').then(
              () => {
                setPw('');
                setPwOffen(false);
              },
            );
          }}
        >
          <label style={{ flex: 1 }}>
            Neues Passwort (Goneo)
            <input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              maxLength={200}
            />
          </label>
          <button className="btn sm pri" type="submit" disabled={busy || !pw}>
            Speichern
          </button>
          <button className="btn sm" type="button" onClick={() => setPwOffen(false)}>
            Abbrechen
          </button>
        </form>
      )}
      {mb.credentials === 'env' && (
        <div className="muted" style={{ fontSize: 12 }}>
          Zugangsdaten liegen in der Server-Umgebung (.env.prod) – Passwort dort ändern.
        </div>
      )}
    </div>
  );
}

// ---------- Postfach hinzufügen ----------
type FormState = {
  address: string;
  displayName: string;
  company: 'dbdm' | 'hoerthin';
  color: MailboxCreate['color'];
  visibility: 'shared' | 'personal';
  password: string;
  historie: '12m' | 'alle' | 'datum';
  datum: string;
  imapUser: string;
  imapHost: string;
  imapPort: string;
  smtpHost: string;
  smtpPort: string;
};
const LEER: FormState = {
  address: '',
  displayName: '',
  company: 'dbdm',
  color: 'blue',
  visibility: 'personal',
  password: '',
  historie: '12m',
  datum: '',
  imapUser: '',
  imapHost: 'imap.goneo.de',
  imapPort: '993',
  smtpHost: 'smtp.goneo.de',
  smtpPort: '465',
};

export function PostfachHinzufuegen() {
  const { me, mailboxes } = useStore((s) => ({ me: s.me, mailboxes: s.mailboxes }));
  const admin = me?.role === 'admin';
  const [offen, setOffen] = useState(false);
  const [f, setF] = useState<FormState>(LEER);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [angelegt, setAngelegt] = useState<number | null>(null);
  const set = (patch: Partial<FormState>) => setF((x) => ({ ...x, ...patch }));

  // Solange ein Postfach „verbindet …“, alle 3 s nachsehen (der Worker meldet Erfolg oder Fehler)
  const wartet = mailboxes.some(verbindetGerade);
  useEffect(() => {
    if (!wartet) return;
    const t = setInterval(() => void loadCounts(), 3000);
    return () => clearInterval(t);
  }, [wartet]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFehler(null);
    const body: MailboxCreate = {
      address: f.address,
      displayName: f.displayName || f.address.split('@')[0] || '',
      company: f.company,
      color: f.color,
      visibility: admin ? f.visibility : 'personal',
      password: f.password,
      ...(f.imapUser.trim() ? { imapUser: f.imapUser } : {}),
      imapHost: f.imapHost,
      imapPort: Number(f.imapPort),
      smtpHost: f.smtpHost,
      smtpPort: Number(f.smtpPort),
      ...(f.historie === 'alle' ? { syncSince: 'alle' } : {}),
      ...(f.historie === 'datum' ? { syncSince: f.datum } : {}),
    };
    const parsed = MailboxCreateSchema.safeParse(body);
    if (!parsed.success) {
      setFehler(parsed.error.issues[0]?.message ?? 'Bitte Eingaben prüfen');
      return;
    }
    if (f.historie === 'datum' && !f.datum) {
      setFehler('Bitte ein Datum wählen');
      return;
    }
    setBusy(true);
    api
      .mailboxCreate(parsed.data)
      .then(async (mb) => {
        setAngelegt(mb.id);
        setF(LEER);
        setOffen(false);
        toast(`${mb.address} angelegt – verbindet …`);
        await reloadMailboxes();
      })
      .catch((e: Error) => setFehler(e.message))
      .finally(() => setBusy(false));
  };

  const neues = angelegt ? mailboxes.find((m) => m.id === angelegt) : null;

  return (
    <div className="pf-add">
      {!offen && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn sm" onClick={() => setOffen(true)} data-testid="pf-add-open">
            + Postfach hinzufügen
          </button>
          {neues && (
            <span className="muted" style={{ fontSize: 12.5 }}>
              {neues.address}: <PostfachStatus mb={neues} />
            </span>
          )}
        </div>
      )}
      {offen && (
        <form className="pf-form" onSubmit={submit} noValidate aria-label="Postfach hinzufügen">
          <div className="s-snip">
            <b>Postfach hinzufügen.</b> Zugangsdaten wie bei Goneo (Benutzername ist die Adresse).
            Das Passwort wird auf dem Server verschlüsselt gespeichert und nie angezeigt; ob es
            stimmt, zeigt der Status nach dem Anlegen.
          </div>
          <div className="konto-row">
            <label style={{ flex: 2 }}>
              E-Mail-Adresse
              <input
                type="email"
                value={f.address}
                onChange={(e) => set({ address: e.target.value })}
                placeholder="lars@minimusiker.de"
                autoComplete="off"
                data-testid="pf-address"
              />
            </label>
            <label style={{ flex: 1 }}>
              Anzeigename
              <input
                value={f.displayName}
                onChange={(e) => set({ displayName: e.target.value })}
                placeholder={f.address.split('@')[0] || 'z. B. Lars'}
                maxLength={80}
              />
            </label>
            <label style={{ flex: 2 }}>
              Passwort (Goneo)
              <input
                type="password"
                autoComplete="new-password"
                value={f.password}
                onChange={(e) => set({ password: e.target.value })}
                maxLength={200}
                data-testid="pf-password"
              />
            </label>
          </div>
          <div className="konto-row">
            <label>
              Firma
              <select
                value={f.company}
                onChange={(e) => set({ company: e.target.value as FormState['company'] })}
              >
                <option value="dbdm">Du bist die Musik UG</option>
                <option value="hoerthin">Hört Hin! GmbH</option>
              </select>
            </label>
            <label>
              Farbe
              <select
                value={f.color}
                onChange={(e) => set({ color: e.target.value as FormState['color'] })}
              >
                {MAILBOX_COLORS.map((c) => (
                  <option key={c} value={c}>
                    {FARBEN[c]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sichtbarkeit
              <select
                value={admin ? f.visibility : 'personal'}
                disabled={!admin}
                title={admin ? undefined : 'Gemeinsame Postfächer legen Admins an'}
                onChange={(e) => set({ visibility: e.target.value as FormState['visibility'] })}
              >
                <option value="personal">persönlich (nur ich)</option>
                <option value="shared">gemeinsam (ganzes Team)</option>
              </select>
            </label>
            <label>
              Gesendet/Archiv spiegeln
              <select
                value={f.historie}
                onChange={(e) => set({ historie: e.target.value as FormState['historie'] })}
              >
                <option value="12m">letzte 12 Monate</option>
                <option value="alle">alles</option>
                <option value="datum">ab Datum …</option>
              </select>
            </label>
            {f.historie === 'datum' && (
              <label>
                ab
                <input
                  type="date"
                  value={f.datum}
                  onChange={(e) => set({ datum: e.target.value })}
                />
              </label>
            )}
          </div>
          <details className="pf-advanced">
            <summary className="muted" style={{ fontSize: 12.5, cursor: 'pointer' }}>
              Erweitert: Benutzername und Server (Standard Goneo)
            </summary>
            <div className="konto-row" style={{ marginTop: 6 }}>
              <label style={{ flex: 2 }}>
                IMAP-Benutzername (leer = Adresse)
                <input
                  value={f.imapUser}
                  onChange={(e) => set({ imapUser: e.target.value })}
                  autoComplete="off"
                />
              </label>
              <label>
                IMAP-Server
                <input value={f.imapHost} onChange={(e) => set({ imapHost: e.target.value })} />
              </label>
              <label>
                Port
                <input
                  value={f.imapPort}
                  onChange={(e) => set({ imapPort: e.target.value })}
                  inputMode="numeric"
                  style={{ width: 64 }}
                />
              </label>
              <label>
                SMTP-Server
                <input value={f.smtpHost} onChange={(e) => set({ smtpHost: e.target.value })} />
              </label>
              <label>
                Port
                <input
                  value={f.smtpPort}
                  onChange={(e) => set({ smtpPort: e.target.value })}
                  inputMode="numeric"
                  style={{ width: 64 }}
                />
              </label>
            </div>
          </details>
          {fehler && (
            <div className="pf-error" role="alert">
              {fehler}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn sm pri" type="submit" disabled={busy} data-testid="pf-submit">
              {busy ? 'Wird angelegt …' : 'Postfach anlegen'}
            </button>
            <button
              className="btn sm"
              type="button"
              onClick={() => {
                setOffen(false);
                setFehler(null);
                setF(LEER);
              }}
            >
              Abbrechen
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Der Worker meldet sich innerhalb weniger Sekunden mit „läuft“ oder einem Fehler.
            </span>
          </div>
        </form>
      )}
    </div>
  );
}
